/**
 * Operators — the learner's pixel identity and customization catalogue.
 *
 * A legacy avatar is nine integers. Each names a part on one of nine rails —
 * build, kit, head, eyes, face, mouth, accessory, ink tone, plate — and the
 * figure is composited from sparse pixel rows and painted off the brand ramp
 * at render time. There is no image: nothing is uploaded, nothing is fetched,
 * nothing is resized, cached, served or moderated, and the whole of somebody's
 * avatar travels as a compact string of digits and dots. Full-body seeds append
 * headwear, props, footwear, garment color and motion under a versioned prefix.
 * The articulated 32×40 renderer lives in avatar-character.ts. That makes it
 * safe to put on the chat wire beside a name (packages/chat/src/avatar.ts is
 * the rule both ends check) and what makes it free to draw two hundred of them
 * in a member list.
 *
 * ## Where it came from
 *
 * `avatar/eos-avatar.js` is the design system as it was delivered: a
 * standalone ES module with the part data, the renderers and a customizer
 * beside it. This file is that module in TypeScript, with the parts verbatim
 * and the seam of the API moved to fit the product:
 *
 *   - the seed prefix, the rail order and the validation rule come from the
 *     protocol package rather than being repeated here, because the Worker
 *     checks a seed it cannot draw and there must be one rule;
 *   - `toElement` and `drawToCanvas` are not ported. The product draws through
 *     one component (components/avatar/Operator.tsx), which uses a data URI,
 *     and an unused renderer is an unused renderer;
 *   - the part *labels* are keys into the string catalogue rather than English
 *     words, because a learner reading Arabic picks their haircut in Arabic
 *     (lib/strings/profile.ts, and the test that keeps the two in step).
 *
 * The pixel rows below are the upstream file's, character for character, with
 * one correction made in both files: the beanie's band spent the accent ten
 * times across the head, against the system's own rule of four cells in a
 * figure, and a solid acid bar at sixteen pixels is the loudest thing in a
 * room of two hundred people. It is a mid-ink band with a two-cell badge now.
 *
 * When a part is added upstream, it is appended here — and append is the only
 * safe edit: an index is the storage format, so inserting a part in the middle
 * silently rewrites every stored seed in the product.
 *
 * ## The five rules the parts are drawn to
 *
 * From the system's own README, and worth repeating where the data is:
 * sixteen cells square at an integer scale; ink off the ramp; the accent
 * appears at most four cells in one figure; eyes composite last, which is why
 * glasses read as glasses; and every new part is reviewed at 16px, because a
 * silhouette that dies at favicon size does not ship.
 *
 * Ink keys: K dark · D outline · M mid · L light · B face fill ·
 * W worn part (tone-derived) · V lens · A brand accent.
 */
import { AVATAR_PREFIX, AVATAR_RAILS, AVATAR_CHARACTER_PREFIX, AVATAR_CHARACTER_RAILS, cleanSeed } from '@engineer-os/chat';
import { CHARACTER_KITS, CHARACTER_HATS, CHARACTER_PROPS, CHARACTER_BOOTS, CHARACTER_COLORS, CHARACTER_POSES, GARMENT_PALETTES } from './avatar-items';

/** One of the nine rails, in the order their indices appear in a seed. */
export type RailId = (typeof AVATAR_CHARACTER_RAILS)[number];

/** Nine indices: which part is worn on each rail. */
export type Selection = Record<RailId, number>;

/** One choosable part. `px` is sparse: a missing row, and `.` in a row, are transparent. */
interface Part {
  key: string;
  label: string;
  /** Levels cleared before this part can be worn. Zero is open to everybody. */
  lvl: number;
  px: string[];
  derived?: false;
  /** Builds only: the chassis the other rails are drawn on top of. */
  base?: string[];
  /** Ink tones only: the face fill and the mid tone this rail repaints. */
  b?: string;
  m?: string;
}

const CHASSIS: string[] = [
  "................",
  "................",
  "....DDDDDDDD....",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "....DBBBBBBD....",
  ".....DBBBBD.....",
  "......BBBB......",
  "...DDDDDDDDDD...",
  "..DBBBBBBBBBBD..",
  "..DBBBBBBBBBBD..",
  "..DBBBBBBBBBBD..",
  "..DBBBBBBBBBBD.."
];

const NARROW = CHASSIS.slice(0, 11).concat([
  "....DDDDDDDD....",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD...",
  "...DBBBBBBBBD..."
]);
const BROAD = CHASSIS.slice(0, 11).concat([
  "..DDDDDDDDDDDD..",
  ".DBBBBBBBBBBBBD.",
  ".DBBBBBBBBBBBBD.",
  ".DBBBBBBBBBBBBD.",
  ".DBBBBBBBBBBBBD."
]);

const BUILD: Part[] = [
  { key: "standard", label: "Standard", lvl: 0, base: CHASSIS, px: [] },
  { key: "narrow", label: "Narrow", lvl: 0, base: NARROW, px: [] },
  { key: "broad", label: "Broad", lvl: 0, base: BROAD, px: [] }
];

const TONE: Part[] = [
  { key: "standard", label: "Standard", lvl: 0, b: "#dcdcdc", m: "#6a6a6a", px: [] },
  { key: "bright", label: "Bright", lvl: 0, b: "#ffffff", m: "#6a6a6a", px: [] },
  { key: "soft", label: "Soft", lvl: 0, b: "#999999", m: "#dcdcdc", px: [] },
  { key: "deep", label: "Deep", lvl: 2, b: "#6a6a6a", m: "#dcdcdc", px: [] },
  { key: 'warm', label: 'Warm', lvl: 0, b: '#e9b895', m: '#5e4036', px: [], derived: false },
  { key: 'brown', label: 'Brown', lvl: 0, b: '#aa7154', m: '#312c32', px: [], derived: false },
  { key: 'umber', label: 'Umber', lvl: 0, b: '#684832', m: '#242328', px: [], derived: false },
  { key: 'rose', label: 'Rose', lvl: 0, b: '#f1ceba', m: '#6a453e', px: [], derived: false }
];

const L = (rows: string[]): string[] => rows;
const HAIR: Part[] = [
  { key: "shaved", label: "Shaved", lvl: 0, px: L([]) },
  { key: "buzz", label: "Buzz", lvl: 0, px: L([
    "", "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...M........M..." ]) },
  { key: "crop", label: "Crop", lvl: 0, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMMMMMMMMM...", "...MM......MM..." ]) },
  { key: "curtain", label: "Curtain", lvl: 2, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMM....MMM...", "...MM......MM...",
    "...MM......MM...", "...MM......MM...", "...MM......MM..." ]) },
  { key: "beanie", label: "Beanie", lvl: 4, px: L([
    "", "....WWWWWWWW....", "...WWWWWWWWWW...", "...MMMMAAMMMM..." ]) },
  { key: "cap", label: "Cap", lvl: 7, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMMMMMMMMM...", "..MMMMMMMMMMMM.." ]) },
  { key: "volume", label: "Volume", lvl: 0, px: L([
    "....MMMMMMMM....", "..MMMMMMMMMMMM..", "..MMMMMMMMMMMM..", "..MMMMMMMMMMMM..", "..MM........MM.." ]) },
  { key: "bun", label: "Bun", lvl: 2, px: L([
    "......MMM.......", "......MMM.......", "....MMMMMMMM....", "...MMMMMMMMMM...", "...M........M..." ]) },
  { key: "tail", label: "Ponytail", lvl: 3, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMMMMMMMMMM..", ".............MM.", ".............MM.", ".............M.." ]) },
  { key: "mohawk", label: "Mohawk", lvl: 5, px: L([
    ".......LL.......", "......LLLL......", "....MMLLLLMM....", "...MMMLLLLMMM..." ]) },
  { key: "hood", label: "Hood up", lvl: 8, px: L([
    "....LLLLLLLL....", "..LLLLLLLLLLLL..", "..LLMMMMMMMMLL..", "..LLM......MLL..",
    "..LL........LL..", "..LL........LL..", "..LL........LL..", "..LLL......LLL.." ]) },
  { key: "bob", label: "Bob", lvl: 0, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMM....MMM...", "...MM......MM...",
    "...MM......MM...", "...MM......MM...", "...MM......MM...", "...MMMM..MMMM..." ]) },
  { key: "long", label: "Long", lvl: 0, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMM....MMM...", "...MM......MM...",
    "...MM......MM...", "...MM......MM...", "...MM......MM...", "...MM......MM...",
    "...MM......MM...", "...MM......MM...", "..MM........MM..", "..MM........MM..",
    "..MM........MM..", "..M..........M.." ]) },
  { key: "braids", label: "Braids", lvl: 0, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MM......MM...", "..MM........MM..",
    "..LM........ML..", "..MM........MM..", "..LM........ML..", "..MM........MM..",
    "..LM........ML.." ]) },
  { key: "puffs", label: "Puffs", lvl: 0, px: L([
    "..MMM......MMM..", ".MMMMM....MMMMM.", ".MMMMMMMMMMMMMM.", "...MMMMMMMMMM...", "...MM......MM..." ]) },
  { key: "pixie", label: "Pixie", lvl: 0, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMMMMMMMMM...", "...MM.....MMM..." ]) },
  { key: "locs", label: "Locs", lvl: 3, px: L([
    "", "....MMMMMMMM....", "...MMMMMMMMMM...", "...MMMMMMMMMM...", "..M.M......M.M..",
    "..M.M......M.M..", "..M.M......M.M..", "..M.M......M.M..", "..M.M......M.M.." ]) },
  { key: "wrap", label: "Headwrap", lvl: 2, px: L([
    "......WW........", "....WWWWWWWW....", "...WWWWWWWWWW...", "...WWWWWWWWWW..." ]) },
  { key: "hijab", label: "Hijab", lvl: 2, px: L([
    "", "....WWWWWWWW....", "...WWWWWWWWWW...", "...WWW....WWW...", "...WW......WW...",
    "...WW......WW...", "...WW......WW...", "...WW......WW...", "...WW......WW...",
    "...WWW....WWW...", "...WWWWWWWWWW...", "..WWWWWWWWWWWW..", "..WW........WW.." ]) }
];

const EYES: Part[] = [
  { key: "scan", label: "Scan", lvl: 0, px: L(["","","","","",".....KK..KK....."]) },
  { key: "dot", label: "Dot", lvl: 0, px: L(["","","","","","......K..K......"]) },
  { key: "dim", label: "Dim", lvl: 0, px: L(["","","","","",".....MM..MM....."]) },
  { key: "wink", label: "Wink", lvl: 3, px: L(["","","","","",".....KK...K....."]) },
  { key: "visor", label: "Visor", lvl: 6, px: L(["","","","","","...KKAAKKKKKK..."]) },
  { key: "goggles", label: "Goggles", lvl: 9, px: L(["","","","","...KKKKKKKKKK...","...KVVKKKKVVK..."]) },
  { key: "wide", label: "Wide", lvl: 0, px: L(["","","","",".....KK..KK.....",".....KK..KK....."]) },
  { key: "closed", label: "Closed", lvl: 0, px: L(["","","","","","....KKK..KKK...."]) },
  { key: "shades", label: "Shades", lvl: 5, px: L(["","","","","...KKKKKKKKKK...","...KKKKKKKKKK..."]) },
  { key: "target", label: "Targeting", lvl: 11, px: L(["","","","","","....KAK..KAK...."]) }
];

const MOUTH: Part[] = [
  { key: "line", label: "Line", lvl: 0, px: L(["","","","","","","","......KK........"]) },
  { key: "flat", label: "Flat", lvl: 0, px: L(["","","","","","","","......MM........"]) },
  { key: "smirk", label: "Smirk", lvl: 0, px: L(["","","","","","","","......KKK......."]) },
  { key: "grin", label: "Grin", lvl: 0, px: L(["","","","","","","",".....KKKK......."]) },
  { key: "open", label: "Open", lvl: 0, px: L(["","","","","","","","......KK........","......MM........"]) },
  { key: "tight", label: "Tight", lvl: 0, px: L(["","","","","","","","......K........."]) },
  { key: "none", label: "None", lvl: 0, px: L([]) }
];

const FACE: Part[] = [
  { key: "none", label: "None", lvl: 0, px: L([]) },
  { key: "stubble", label: "Stubble", lvl: 0, px: L(["","","","","","","","","....MMMMMMMM....","......MMMM......"]) },
  { key: "beard", label: "Beard", lvl: 2, px: L(["","","","","","","","...K........K...","....KKKKKKKK....","......KKKK......"]) },
  { key: "freckles", label: "Freckles", lvl: 0, px: L(["","","","","","","....M......M...."]) },
  { key: "scar", label: "Scar", lvl: 4, px: L(["","","","","....K...........","","....K..........."]) },
  { key: "lashes", label: "Lashes", lvl: 0, px: L(["","","","","....K.K..K.K...."]) },
  { key: "brows", label: "Brows", lvl: 0, px: L(["","","","","....KK....KK...."]) },
  { key: "mark", label: "Forehead mark", lvl: 3, px: L(["","","","",".......A........"]) }
];

const KIT: Part[] = [
  { key: "hoodie", label: "Hoodie", lvl: 0, px: L(["","","","","","","","","","","",
    "", ".....KKKKKK.....", "......A..A......"]) },
  { key: "tee", label: "Tee", lvl: 0, px: L(["","","","","","","","","","","","",
    "....A..........."]) },
  { key: "shirt", label: "Shirt", lvl: 0, px: L(["","","","","","","","","","","","",
    ".....K....K.....", "......K..K......", ".......KK......."]) },
  { key: "jacket", label: "Jacket", lvl: 5, px: L(["","","","","","","","","","","",
    "", ".......KA.......", ".......K........", ".......K........", ".......K........"]) },
  { key: "lanyard", label: "Lanyard", lvl: 10, px: L(["","","","","","","","","","","",
    "", ".....K....K.....", ".....K....K.....", "......AAAA......"]) },
  { key: "turtleneck", label: "Turtleneck", lvl: 0, px: L(["","","","","","","","","","","",
    "", "...KKKKKKKKKK..."]) },
  { key: "flannel", label: "Flannel", lvl: 0, px: L(["","","","","","","","","","","",
    "", ".....K....K.....", ".....K....K.....", "...KKKKKKKKKK...", ".....K....K....."]) },
  { key: "vest", label: "Vest", lvl: 3, px: L(["","","","","","","","","","","",
    "", "...KK......KK...", "...KK......KK...", "...KK......KK...", "...KK......KK..."]) },
  { key: "overalls", label: "Overalls", lvl: 6, px: L(["","","","","","","","","","","",
    "", ".....K....K.....", ".....KKKKKK.....", ".....K.AA.K....."]) },
  { key: "hivis", label: "Hi-vis", lvl: 8, px: L(["","","","","","","","","","","",
    "", "...AA......AA..."]) },
  { key: "tank", label: "Tank", lvl: 0, px: L(["","","","","","","","","","","",
    "", "....KK....KK....", "....KK....KK...."]) },
  { key: "blouse", label: "Blouse", lvl: 0, px: L(["","","","","","","","","","","",
    "", "...KK......KK...", "....KK....KK....", ".....KKKKKK....."]) },
  { key: "wraptop", label: "Wrap top", lvl: 3, px: L(["","","","","","","","","","","",
    "", "...K........K...", "....KK....KK....", ".....KKKKKK.....", "......KKKK......"]) },
  { key: "scarf", label: "Scarf", lvl: 5, px: L(["","","","","","","","","","","",
    "", "...MMMMMMMMMM...", ".......MM.......", ".......MM.......", ".......MM......."]) },
  ...CHARACTER_KITS
];

const ACC: Part[] = [
  { key: "none", label: "None", lvl: 0, px: L([]) },
  { key: "glasses", label: "Glasses", lvl: 0, px: L(["","","","","","...MVVVMMVVVM..."]) },
  { key: "stud", label: "Stud", lvl: 6, px: L(["","","","","","","","...A............"]) },
  { key: "cans", label: "Headphones", lvl: 4, px: L(["",
    "....MMMMMMMM....", "", "", "..MM........MM..", "..MM........MM..", "..MM........MM.."]) },
  { key: "band", label: "Headband", lvl: 3, px: L(["","","","...WWWWWWWWWW..."]) },
  { key: "wire", label: "Earbud", lvl: 5, px: L(["","","","","","","..M.............","..M............."]) },
  { key: "patch", label: "Eye patch", lvl: 7, px: L(["","","","","...KKKK.........","...KKKK........."]) },
  { key: "pin", label: "Shoulder pin", lvl: 2, px: L(["","","","","","","","","","","","","...A............"]) },
  { key: "hoops", label: "Hoops", lvl: 0, px: L(["","","","","","","","...A........A..."]) },
  { key: "drops", label: "Drop earrings", lvl: 3, px: L(["","","","","","","","..W.........W...","..W.........W..."]) },
  { key: "chain", label: "Chain", lvl: 2, px: L(["","","","","","","","","","","","",".....KAAK......."]) },
  { key: "clip", label: "Hair clip", lvl: 0, px: L(["","","","....A..........."]) }
];

const PLATE: Part[] = [
  { key: "hairline", label: "Hairline", lvl: 0, px: L([]) },
  { key: "corners", label: "Corners", lvl: 3, px: L([]) },
  { key: "dots", label: "Dot grid", lvl: 8, px: L([]) },
  { key: "lines", label: "Scanline", lvl: 5, px: L([]) },
  // Append only: existing seeds must keep the same frame.
  { key: "lift", label: "Power up", lvl: 1, px: L([]) },
  { key: "nod", label: "Ready nod", lvl: 4, px: L([]) },
  { key: "spark", label: "Signal burst", lvl: 8, px: L([]) }
];


const SETS: Record<RailId, Part[]> = { build: BUILD, kit: KIT, hair: HAIR, eyes: EYES, face: FACE, mouth: MOUTH, acc: ACC, tone: TONE, plate: PLATE, hat: CHARACTER_HATS, prop: CHARACTER_PROPS, boots: CHARACTER_BOOTS, color: CHARACTER_COLORS, pose: CHARACTER_POSES };

/**
 * The order a rail is offered in, which is not the order its index sits in a
 * seed. `AVATAR_RAILS` is the storage order and may never be rearranged; this
 * is the builder's, and it runs from the parts that decide the silhouette to
 * the ones that decide the surface.
 */
const ORDER: RailId[] = ['kit', 'color', 'hat', 'prop', 'boots', 'pose', 'build', 'hair', 'eyes', 'face', 'mouth', 'acc', 'tone', 'plate'];

/**
 * Which layer is painted over which. Eyes last, always: nothing may overwrite
 * them, and that is the whole reason glasses read as glasses and a visor
 * reads as a visor rather than as a bar across a face.
 */
const LAYER_ORDER: RailId[] = ['kit', 'hair', 'hat', 'face', 'mouth', 'acc', 'eyes'];

const DARK: Record<string, string> = { K: '#070707', D: '#242424', M: '#6a6a6a', L: '#999999', B: '#dcdcdc', W: '#ffffff', V: '#ffffff' };
const LIGHT: Record<string, string> = { K: '#000000', D: '#000000', M: '#6a6a6a', L: '#9a9a9a', B: '#ffffff', W: '#000000', V: '#ffffff' };
/** The four inks a worn part may take, so that a hat is never the tone of the head under it. */
const WEARABLE = ['#ffffff', '#dcdcdc', '#999999', '#6a6a6a'];
const lum = (h: string): number => parseInt(h.slice(1, 3), 16);

/** Signal Green, and the one place in a figure it is allowed to appear. */
const DEFAULT_ACCENT = '#a3f01c';
/** Green as type rather than as fill; #a3f01c on white is 1.4:1 (styles/tokens.css). */
const LIGHT_ACCENT = '#3f6b00';

export interface RenderOptions {
  /** Pixels per cell. Always an integer: a fractional scale puts cell edges on half-pixels and the figure goes soft. */
  scale?: number;
  /** Invert for the light theme and for print. */
  light?: boolean;
  /** The one colour. Defaults to Signal Green, or to its light-theme counterpart. */
  accent?: string;
  /** The accessible name, for a figure that is not decoration. */
  label?: string;
}

/* ── The seed ────────────────────────────────────────────────────────────── */

/**
 * A seed as a selection. Out of range falls to part zero rather than to
 * nothing, so a seed written by a build with more parts than this one still
 * draws a person — a standard haircut is a better answer than a blank.
 */
export function parseSeed(seed: string | null | undefined): Selection {
  const clean = cleanSeed(seed);
  const body = (clean ?? '').replace(/^eos avatar set (16|32)\./, '');
  const nums = body.split('.').map(Number);
  const sel = {} as Selection;
  AVATAR_CHARACTER_RAILS.forEach((rail, i) => {
    const n = nums[i];
    sel[rail] = Number.isInteger(n) && n !== undefined && n >= 0 && n < SETS[rail].length ? n : 0;
  });
  return sel;
}

/** A selection as the canonical seed. What is stored, sent and compared. */
export function toSeed(sel: Selection): string {
  if (AVATAR_CHARACTER_RAILS.slice(AVATAR_RAILS.length).some((rail) => (sel[rail] ?? 0) !== 0)) {
    return AVATAR_CHARACTER_PREFIX + AVATAR_CHARACTER_RAILS.map((rail) => sel[rail] ?? 0).join('.');
  }
  return AVATAR_PREFIX + AVATAR_RAILS.map((rail) => sel[rail] ?? 0).join('.');
}

/** True when two selections are the same figure. */
export function sameSelection(a: Selection, b: Selection): boolean {
  return AVATAR_CHARACTER_RAILS.every((rail) => (a[rail] ?? 0) === (b[rail] ?? 0));
}

/**
 * The figure somebody has before they have built one, from their id.
 *
 * Everybody is somebody from their first message, without being asked, and it
 * is the same figure on every device a signed-in person opens: the id hashed
 * here is the account's where there is one, and the browser's where there is
 * not (state/useChat.ts).
 *
 * Deliberately *not* filtered by what the learner has unlocked, which is where
 * this parts from the upstream module's signature. A derived figure that drew
 * from a widening pool would change the day somebody cleared a level — their
 * face would be replaced by a stranger's as a reward for playing — and an
 * identity nobody chose has to at least be an identity that holds still. The
 * pool is the parts open to everyone, so the figure it gives is one the
 * learner could have built on their first day and can keep for ever.
 */
export function selectionFromId(id: string): Selection {
  const text = String(id);
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const sel = Object.fromEntries(AVATAR_CHARACTER_RAILS.map((rail) => [rail, 0])) as Selection;
  AVATAR_RAILS.forEach((rail, i) => {
    const open = SETS[rail].filter((p) => p.lvl === 0 && p.derived !== false);
    h = Math.imul(h ^ (i + 1), 16777619);
    const part = open[Math.abs(h) % open.length];
    sel[rail] = part ? SETS[rail].indexOf(part) : 0;
  });
  return sel;
}

/** A figure built from whatever this learner has open. The builder's dice. */
export function randomSelection(level: number): Selection {
  const sel = {} as Selection;
  for (const rail of AVATAR_CHARACTER_RAILS) {
    const open = SETS[rail].filter((_, index) => isPartUnlocked(rail, index, level));
    const part = open[Math.floor(Math.random() * open.length)];
    sel[rail] = part ? SETS[rail].indexOf(part) : 0;
  }
  return sel;
}

/* ── What is open ────────────────────────────────────────────────────────── */

/**
 * Levels cleared before a part can be worn, and zero for the ones that are
 * open from the first minute — which is most of them, on every rail. What is
 * locked is never a way to look like a person: it is the hood, the visor, the
 * hi-vis, the targeting eyes. Nobody is kept from a haircut by the course.
 */
export function unlockLevel(rail: RailId, index: number): number {
  // Identity is available on day one. Keep the original lvl values in the
  // part data: selectionFromId uses that original pool, so opening these
  // choices must not replace anybody's default face after an update.
  if (rail === 'tone' || rail === 'face' || rail === 'mouth' || rail === 'build') return 0;
  if (rail === 'hair' && !['beanie', 'cap', 'hood'].includes(SETS[rail][index]?.key ?? '')) return 0;
  if (rail === 'eyes' && SETS[rail][index]?.key === 'wink') return 0;
  if (rail === 'acc' && SETS[rail][index]?.key === 'patch') return 0;
  return SETS[rail][index]?.lvl ?? 0;
}

export function isPartUnlocked(rail: RailId, index: number, level: number): boolean {
  return unlockLevel(rail, index) <= level;
}

/** Every locked part in a selection, so the builder can say what it will not save. */
export function lockedParts(sel: Selection, level: number): RailId[] {
  return AVATAR_CHARACTER_RAILS.filter((rail) => !isPartUnlocked(rail, sel[rail] ?? 0, level));
}

/**
 * The same figure with everything the learner has not earned put back to a
 * part they have. The seed a locked-out browser is given rather than a
 * refusal: a stored avatar outlives a reset of the record, and somebody who
 * starts the course again should not be faceless for eleven levels.
 */
export function withinReach(sel: Selection, level: number): Selection {
  const out = {} as Selection;
  for (const rail of AVATAR_CHARACTER_RAILS) {
    const index = sel[rail] ?? 0;
    out[rail] = isPartUnlocked(rail, index, level) ? index : 0;
  }
  return out;
}

/* ── The catalogue ───────────────────────────────────────────────────────── */

export interface CataloguePart {
  index: number;
  /** The key the string catalogue names this part by (lib/strings/profile.ts). */
  key: string;
  /** The English word the design system shipped, and the fallback for a key nobody has translated. */
  label: string;
  /** Levels cleared before it can be worn. */
  level: number;
}

export interface Rail {
  id: RailId;
  parts: CataloguePart[];
}

/** The rails and their parts, in the order the builder offers them. */
export const CATALOGUE: Rail[] = ORDER.map((id) => ({
  id,
  parts: SETS[id].map((p, index) => ({ index, key: p.key, label: p.label, level: unlockLevel(id, index) })),
}));

export interface AvatarReward extends CataloguePart { rail: RailId }

/** One source for the builder, the profile and completion rewards. */
export function avatarProgression(level: number) {
  const cleared = Number.isFinite(level) ? Math.max(0, Math.floor(level)) : 0;
  const parts = CATALOGUE.flatMap((r) => r.parts.map((p) => ({ ...p, rail: r.id })));
  const milestones = [...new Set(parts.filter((p) => p.level > 0).map((p) => p.level))].sort((a, b) => a - b);
  const latestLevel = milestones.filter((n) => n <= cleared).at(-1) ?? 0;
  const nextLevel = milestones.find((n) => n > cleared) ?? null;
  return {
    cleared,
    available: parts.filter((p) => p.level <= cleared).length,
    total: parts.length,
    latestLevel,
    latest: parts.filter((p) => p.level === latestLevel && p.level > 0),
    nextLevel,
    next: parts.filter((p) => p.level === nextLevel),
    remaining: nextLevel === null ? 0 : nextLevel - cleared,
    fraction: nextLevel === null ? 1 : (cleared - latestLevel) / (nextLevel - latestLevel),
  };
}

/** Existing earned items survive a record reset; new locked choices are previews. */
export function lockedChanges(sel: Selection, worn: Selection, level: number): RailId[] {
  return lockedParts(sel, level).filter((rail) => sel[rail] !== worn[rail]);
}

/** Which frame the figure asks its container for: hairline, corners, dots or lines. */
export function plateOf(sel: Selection): string {
  return PLATE[sel.plate ?? 0]?.key ?? 'hairline';
}

/* ── Drawing ─────────────────────────────────────────────────────────────── */

/**
 * The ink for one figure. The tone rail repaints the face fill and the mid
 * tone, and the worn ink is then chosen to be the furthest of the four from
 * both — which is what stops a beanie disappearing into the head it is on.
 *
 * The light theme takes the fixed palette instead: a tone ramp designed
 * against black does not invert, and a figure in an article or on a printed
 * page is a silhouette rather than a portrait.
 */
function palette(sel: Selection, opts: RenderOptions): Record<string, string> {
  const pal: Record<string, string> = {
    ...(opts.light ? LIGHT : DARK),
    ...(GARMENT_PALETTES[sel.color ?? 0] ?? GARMENT_PALETTES[0]),
    H: '#dfb958', h: '#8b6e32',
    A: opts.accent ?? (opts.light ? LIGHT_ACCENT : DEFAULT_ACCENT),
  };
  if (!opts.light || (sel.tone ?? 0) >= 4) {
    const tone = TONE[sel.tone ?? 0] ?? TONE[0]!;
    pal.B = tone.b!;
    pal.M = tone.m!;
    const bl = lum(pal.B);
    const ml = lum(pal.M);
    pal.W = WEARABLE.map((c) => ({ c, d: Math.min(Math.abs(lum(c) - bl), Math.abs(lum(c) - ml)) })).sort((x, y) => y.d - x.d)[0]!.c;
    pal.V = pal.B === '#ffffff' ? '#dcdcdc' : '#ffffff';
  }
  return pal;
}

/** A selection composited into a 16×16 grid of ink keys. `.` is empty. */
export function toGrid(sel: Selection): string[][] {
  const base = (BUILD[sel.build ?? 0] ?? BUILD[0]!).base ?? [];
  const g = base.map((r) => r.split(''));
  if ((sel.color ?? 0) > 0 || sel.kit >= 14) {
    for (let y = 11; y < 16; y++) g[y] = g[y]!.map((c) => c === 'B' ? 'G' : c);
  }
  for (const id of LAYER_ORDER) {
    const opt = SETS[id][sel[id] ?? 0];
    if (!opt) continue;
    opt.px.forEach((row, y) => {
      if (y > 15 || !row) return;
      const line = g[y];
      if (!line) return;
      for (let x = 0; x < 16 && x < row.length; x++) {
        const ch = row[x];
        if (ch && ch !== '.') line[x] = ch;
      }
    });
  }
  return g;
}

/** The grid resolved to colours. Null is transparent. */
export function toPixels(sel: Selection, opts: RenderOptions = {}): (string | null)[][] {
  const pal = palette(sel, opts);
  return toGrid(sel).map((row) => row.map((c) => (c && c !== '.' && pal[c] ? pal[c]! : null)));
}

/**
 * One figure as an SVG document.
 *
 * Rectangles rather than a path, because the whole thing is at most 256 of
 * them and a rect per cell is what makes `shape-rendering: crispEdges` exact
 * at every size. No background: the figure sits on whatever surface it is
 * drawn on, which is how one seed works in a dark room and a light one.
 */
export function toSVG(sel: Selection, opts: RenderOptions = {}): string {
  const s = opts.scale ?? 1;
  const px = toPixels(sel, opts);
  const rects: string[] = [];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const c = px[y]?.[x];
      if (c) rects.push(`<rect x="${x}" y="${y}" width="1" height="1" fill="${c}"/>`);
    }
  }
  const label = (opts.label ?? '').replace(/[<>&"]/g, '');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${16 * s}" height="${16 * s}" viewBox="0 0 16 16" ` +
    `shape-rendering="crispEdges" role="img" aria-label="${label}">${rects.join('')}</svg>`
  );
}

/**
 * The same, as a data URI for an `<img>`.
 *
 * Memoised, because the member list and the message list draw the same dozen
 * figures over and over as the room scrolls, and building the string is two
 * hundred rectangles of concatenation. The cache is keyed on everything that
 * changes the bytes and is bounded: a room holds two hundred sockets, a page
 * of messages holds fifty, and past that the oldest entries are dropped.
 */
const uris = new Map<string, string>();
const URI_CACHE_MAX = 600;

export function toDataURI(sel: Selection, opts: RenderOptions = {}): string {
  const key = `${toSeed(sel)}|${opts.scale ?? 1}|${opts.light ? 'l' : 'd'}|${opts.accent ?? ''}`;
  const hit = uris.get(key);
  if (hit) return hit;
  // The label is deliberately not in the key and not in the drawing: an image
  // used as a picture carries its name in the `alt` the caller writes, and
  // putting one in here would be a cache entry per person per language.
  const uri = 'data:image/svg+xml;utf8,' + encodeURIComponent(toSVG(sel, { ...opts, label: '' }));
  if (uris.size >= URI_CACHE_MAX) uris.clear();
  uris.set(key, uri);
  return uri;
}

/* ── The seed as it travels ──────────────────────────────────────────────── */

/**
 * A stored or received string as a seed this build can draw, or null.
 *
 * The protocol's gate first — shape only, and the same check the Worker runs
 * — then through the renderer's own parse and back out, which is what pins an
 * index past the end of a rail to a part that exists. So what comes back is
 * always drawable and always canonical, and a browser one release behind
 * stores the seed it can actually see.
 */
export function readSeed(raw: unknown): string | null {
  const clean = cleanSeed(raw);
  return clean === null ? null : toSeed(parseSeed(clean));
}
